import { api } from 'src/store/api';
import FileDownload from 'js-file-download';
import { type Collective } from '../types/collective';
import { type ActionContractsResponse, type ContractIdentifier } from './contract';

export type DownloadCollectiveEmployeesFileParams = {
  id: string;
  name: string;
};

type UploadCollectiveAssignmentFileParams = {
  file: File;
  onlyValidate?: boolean;
};

export type CollectiveAssignmentUploadResponse = {
  notFound: Array<ContractIdentifier & { message: string }>;
} & ActionContractsResponse;

const extendedApi = api.injectEndpoints({
  endpoints: build => ({
    getCollectives: build.query<Collective[], void>({
      query: () => ({
        url: '/collectives'
      }),
      providesTags: ['Collective']
    }),
    createCollective: build.mutation<Collective, { name: string }>({
      query: body => ({ url: '/collectives', method: 'POST', body }),
      invalidatesTags: ['Collective']
    }),
    renameCollective: build.mutation<Collective, { id: string; name: string }>({
      query: ({ id, name }) => ({ url: `/collectives/${id}`, method: 'PATCH', body: { name } }),
      invalidatesTags: ['Collective']
    }),
    deleteCollective: build.mutation<void, { id: string }>({
      query: ({ id }) => ({ url: `/collectives/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Collective']
    }),
    uploadCollectiveAssignmentFile: build.mutation<
      CollectiveAssignmentUploadResponse,
      UploadCollectiveAssignmentFileParams
    >({
      query: ({ file, onlyValidate }) => ({
        url: '/collectives/assignment-file',
        method: 'POST',
        headers: { 'Content-type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
        params: { onlyValidate },
        body: file
      }),
      invalidatesTags: (_result, _error, arg) => (arg.onlyValidate ? [] : ['Collective', 'Employee'])
    }),
    downloadCollectiveEmployeesFile: build.mutation<unknown, DownloadCollectiveEmployeesFileParams>({
      queryFn: async ({ id, name }, _api, _extraOptions, baseQuery) => {
        try {
          const result = await baseQuery({
            url: `/collectives/${id}/employees-file`,
            responseHandler: async response => await response.blob(),
            cache: 'no-cache'
          });

          const blobData = (await result.data) as Blob;
          if (!blobData) {
            throw new Error();
          }

          FileDownload(blobData, `${name}.xlsx`, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');

          return result;
        } catch (error) {
          console.error('Error downloading file:', error);
          throw error;
        }
      }
    })
  }),
  overrideExisting: false
});

export const {
  useGetCollectivesQuery,
  useDownloadCollectiveEmployeesFileMutation,
  useCreateCollectiveMutation,
  useRenameCollectiveMutation,
  useDeleteCollectiveMutation,
  useUploadCollectiveAssignmentFileMutation
} = extendedApi;
